import React, { useState } from 'react';
import { useP } from '../i18n/useP';
import { usePersistentValueLocal } from '../hooks/usePersistentState';
import './Card.css';
import '../styles/designSystem.css';

/**
 * CardShell — STANDARD DE CARTE (proto validé le 2026-08-27).
 *
 * Sept zones, ordre fixe. Une zone vide disparaît, les autres ne bougent pas.
 *
 *   1 Titre            entête, gauche (+ sous-titre de contexte)
 *   2 Contrôles de vue entête, à DROITE (segmentés exclusifs)
 *   3 Action primaire  entête, à droite des contrôles de vue
 *   4 Dépendants       entête, entre vue et action (Grouper quand Vue = Frise)
 *   5 Filtres          toolbar, gauche (pastilles, segmentés, compteur)
 *   6 Portée+recherche toolbar, droite : la recherche est TOUJOURS le dernier
 *   7 Synthèse         bande KPI entre toolbar et corps
 *
 * L'ENTÊTE TIENT UNE SEULE LIGNE (2026-08-30) : contrôles de vue, contrôles
 * dépendants, actions, puis l'entonnoir tout à droite. Une seconde ligne a
 * existé trois jours pour les contrôles dépendants ; elle faisait sauter la
 * ligne du titre dès qu'un contrôle apparaissait, et découpait en deux ce qui
 * se lit d'un regard.
 *
 * Règle de classement d'un contrôle : s'il change la REPRÉSENTATION ou la base
 * de calcul sans réduire les lignes, il va dans l'entête ; s'il RÉDUIT ou cible
 * les lignes, il va dans la toolbar.
 *
 * `collapsible` (2026-08-27) : la toolbar se REPLIE. Un bouton entonnoir dans
 * l'entête l'ouvre et la referme, et porte le compte de filtres actifs
 * (`filterCount`). La carte reste courte quand on ne filtre pas, et la ligne de
 * titre ne bouge pas pour autant. Avec `storageKey`, l'état ouvert/replié est
 * RETENU d'une visite à l'autre : ouvrir sa barre à chaque écran serait un
 * péage. Sans clé, il ne vit que le temps de l'écran.
 *
 * BARRE D'ÉTAT : dès qu'un filtre est actif, la toolbar prend l'aplat ardoise
 * clair. La couleur ne décore pas, elle DIT qu'on ne lit qu'une partie du
 * tableau — même geste que « Actifs » en terracotta. Un lecteur qui reprend
 * l'écran trois semaines plus tard voit que le total est filtré.
 *
 * Aucune logique métier ici : ce sont des emplacements, rien d'autre. La charte
 * ne change pas, seuls les tokens existants servent.
 */
export default function CardShell({
  title,
  subtitle,
  viewControls = null,
  primaryAction = null,
  dependentControls = null,
  filters = null,
  scope = null,
  search = null,
  summary = null,
  filterCount = 0,
  collapsible = false,
  storageKey = null,
  flush = false,
  children,
  className = '',
  bodyClassName = '',
}) {
  const p = useP();
  const hasToolbar = Boolean(filters || scope || search);
  /* Ouverte d'emblée s'il y a déjà de quoi filtrer : une barre repliée qui
     cache un filtre actif ferait mentir le tableau. Ensuite, c'est le choix de
     la personne qui fait foi, et il est retenu. */
  const initialOpen = !collapsible || filterCount > 0;
  const [openMem, setOpenMem] = usePersistentValueLocal(
    storageKey ? `lk_filters_open_${storageKey}` : '__lk_filters_noop__', initialOpen);
  const [openLocal, setOpenLocal] = useState(initialOpen);
  const open = storageKey ? openMem : openLocal;
  const setOpen = storageKey ? setOpenMem : setOpenLocal;
  const showToggle = collapsible && hasToolbar;
  const hasHead = title != null || subtitle != null || viewControls || primaryAction || showToggle;
  const funnel = showToggle ? (
    <button
      type="button"
      className={`lk-funnel${open ? ' is-open' : ''}${filterCount > 0 ? ' is-active' : ''}`}
      aria-expanded={open}
      onClick={() => setOpen(v => !v)}
      title={open ? p('Masquer les filtres') : p('Afficher les filtres')}
    >
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
      </svg>
      {filterCount > 0
        ? `${p('Filtres')} · ${filterCount}`
        : (open ? p('Filtres') : p('Filtrer'))}
    </button>
  ) : null;
  return (
    <section className={`std-card${className ? ` ${className}` : ''}`}>
      {hasHead && (
        <div className="lk-cs__hd">
          <div className="lk-cs__hd1">
            <div>
              {title != null && <p className="lk-cs__ttl">{title}</p>}
              {/* Un sous-titre peut porter une COMMANDE sous le titre (la bascule
                  « Saisie » des Prévisions, demande d'Olivier, 2026-10-10) : elle
                  se rend alors dans un bloc, un <p> ne pouvant contenir de bloc. */}
              {subtitle != null && (React.isValidElement(subtitle)
                ? <div className="lk-cs__sub lk-cs__sub--ctl">{subtitle}</div>
                : <p className="lk-cs__sub">{subtitle}</p>)}
            </div>
            {(viewControls || dependentControls || funnel || primaryAction) && (
              <div className="lk-cs__hd-r">
                {viewControls}
                {dependentControls}
                {primaryAction}
                {/* L'entonnoir FERME l'entête (2026-08-27) : il ouvre la bande
                    du dessous, sa place est contre elle, donc tout à droite. */}
                {funnel}
              </div>
            )}
          </div>
        </div>
      )}
      {hasToolbar && (
        <div className={`lk-cs__tbwrap${showToggle && !open ? ' is-closed' : ''}`}>
          <div className="lk-cs__tbclip">
            <div className={`lk-cs__tb${filterCount > 0 ? ' is-active' : ''}`}>
              <div className="lk-cs__tb-l">{filters}</div>
              <div className="lk-cs__tb-r">{scope}{search}</div>
            </div>
          </div>
        </div>
      )}
      {summary && <div className="lk-cs__kpi">{summary}</div>}
      <div className={`std-card__body${flush ? ' std-card__body--flush' : ''}${bodyClassName ? ` ${bodyClassName}` : ''}`}>
        {children}
      </div>
    </section>
  );
}
